import React, { useState, useEffect } from 'react';
import { Pressable, ScrollView, Alert, Platform } from 'react-native';
import { 
  YStack, 
  XStack, 
  H4, 
  Text, 
  Button, 
  Input, 
  TextArea, 
  Select, 
  Adapt, 
  Sheet,
  Label,
  RadioGroup,
  View,
  Separator,
  Card,
  Progress,
  Spinner,
  Dialog,
  H2,
  SizableText
} from 'tamagui';
import { Badge } from '@mui/material';
import { AntDesign, MaterialCommunityIcons, Ionicons, Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apis from '@api/api';
import * as Animatable from 'react-native-animatable';
import { MotiView } from 'moti';

interface IntegrationRequest {
  _id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  voteCount: number;
  totalVotes: number;
  userVote: 'up' | 'down' | null;
  requestedBy: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

interface PopularityData {
  category: string;
  count: number;
  avgVotes: number;
}

interface IntegrationRequestHeaderProps {
  onRequestSubmitted?: () => void;
}

const IntegrationRequestHeader: React.FC<IntegrationRequestHeaderProps> = ({ 
  onRequestSubmitted 
}) => {
  const { t } = useTranslation();
  const theme = themeSelector();
  const queryClient = useQueryClient();
  
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [showPopularityGraph, setShowPopularityGraph] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other',
    tags: '',
    apiEndpoint: '',
    documentation: '',
    estimatedComplexity: 'medium',
    businessValue: 'medium'
  });

  const colors = {
    background: theme ? '#ffffff' : '#1f2937',
    cardBackground: theme ? '#f8fafc' : '#374151',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    border: theme ? '#e5e7eb' : '#4b5563',
    accent: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444'
  };

  // Fetch popular integration requests
  const { data: popularRequests, isLoading: isLoadingPopular } = useQuery({
    queryKey: ['popularIntegrationRequests', selectedCategory],
    queryFn: () => apis.getPopularIntegrationRequests(selectedCategory === 'all' ? undefined : selectedCategory),
    enabled: showPopularityGraph
  });

  // Fetch integration request stats
  const { data: stats } = useQuery({
    queryKey: ['integrationRequestStats'],
    queryFn: apis.getIntegrationRequestStats
  });

  // Create integration request mutation
  const createRequestMutation = useMutation({
    mutationFn: apis.createIntegrationRequest,
    onSuccess: (response) => {
      if (response.data?.success) {
        Alert.alert('Success', 'Integration request submitted successfully!');
        setFormData({
          title: '',
          description: '',
          category: 'other',
          tags: '',
          apiEndpoint: '',
          documentation: '',
          estimatedComplexity: 'medium',
          businessValue: 'medium'
        });
        setShowRequestForm(false);
        queryClient.invalidateQueries({ queryKey: ['popularIntegrationRequests'] });
        queryClient.invalidateQueries({ queryKey: ['integrationRequestStats'] });
        onRequestSubmitted?.();
      }
    },
    onError: (error: any) => {
      if (error.response?.status === 409) {
        const similarRequests = error.response.data.similarRequests;
        Alert.alert(
          'Similar Request Found',
          `A similar integration request already exists. Would you like to vote on it instead?`,
          [
            { text: 'Cancel', style: 'cancel' },
            { 
              text: 'View Similar', 
              onPress: () => {
                setShowRequestForm(false);
                setShowPopularityGraph(true);
              }
            }
          ]
        );
      } else {
        Alert.alert('Error', 'Failed to submit integration request');
      }
    }
  });

  // Vote mutation
  const voteMutation = useMutation({
    mutationFn: ({ requestId, voteType }: { requestId: string; voteType: 'up' | 'down' }) =>
      apis.voteOnIntegrationRequest(requestId, voteType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['popularIntegrationRequests'] });
      queryClient.invalidateQueries({ queryKey: ['integrationRequestStats'] });
    }
  });

  const handleSubmitRequest = () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      Alert.alert('Error', 'Title and description are required');
      return;
    }

    createRequestMutation.mutate(formData);
  };

  const handleVote = (requestId: string, voteType: 'up' | 'down') => {
    voteMutation.mutate({ requestId, voteType });
  };

  const categoryOptions = [
    { label: 'Sales', value: 'sales' },
    { label: 'Marketing', value: 'marketing' },
    { label: 'Support', value: 'support' },
    { label: 'Productivity', value: 'productivity' },
    { label: 'Analytics', value: 'analytics' },
    { label: 'Communication', value: 'communication' },
    { label: 'Other', value: 'other' }
  ];

  const complexityOptions = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' }
  ];

  const businessValueOptions = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' }
  ];

  return (
    <YStack gap="$3" padding="$2">
      {/* Header with Stats */}
      <XStack flexWrap='wrap' justifyContent="space-between" alignItems="center">
        <YStack>
          <H4 color={colors.text} fontSize="$5" fontWeight="600">
            Integration Requests
          </H4>
          <Text color={colors.textSecondary} fontSize="$3">
            {stats?.data?.overview?.totalRequests || 0} total requests
          </Text>
        </YStack>
        
        <XStack gap="$2">
          <Button
            size="$3"
            variant="outlined"
            borderColor={colors.border}
            backgroundColor={colors.cardBackground}
            onPress={() => setShowPopularityGraph(!showPopularityGraph)}
          >
            <MaterialCommunityIcons 
              name="chart-line" 
              size={16} 
              color={colors.text} 
            />
            <Text color={colors.text} marginLeft="$2">
              Popular
            </Text>
          </Button>
          
          <Button
            size="$3"
            backgroundColor={colors.accent}
            color="white"
            onPress={() => setShowRequestForm(true)}
          >
            <AntDesign name="plus" size={16} color="white" />
            <Text color="white" marginLeft="$2">
              Request
            </Text>
          </Button>
        </XStack>
      </XStack>

      {/* Stats Cards */}
      {stats?.data?.overview && (
        <XStack gap="$3" flexWrap="wrap">
          <Card backgroundColor={colors.cardBackground} padding="$3" flex={1} minWidth={120}>
            <XStack alignItems="center" gap="$2">
              <MaterialCommunityIcons name="clock-outline" size={20} color={colors.warning} />
              <YStack>
                <Text color={colors.text} fontSize="$2" fontWeight="600">
                  {stats.data.overview.pendingRequests}
                </Text>
                <Text color={colors.textSecondary} fontSize="$1">
                  Pending
                </Text>
              </YStack>
            </XStack>
          </Card>
          
          <Card backgroundColor={colors.cardBackground} padding="$3" flex={1} minWidth={120}>
            <XStack alignItems="center" gap="$2">
              <MaterialCommunityIcons name="progress-clock" size={20} color={colors.accent} />
              <YStack>
                <Text color={colors.text} fontSize="$2" fontWeight="600">
                  {stats.data.overview.inProgressRequests}
                </Text>
                <Text color={colors.textSecondary} fontSize="$1">
                  In Progress
                </Text>
              </YStack>
            </XStack>
          </Card>
          
          <Card backgroundColor={colors.cardBackground} padding="$3" flex={1} minWidth={120}>
            <XStack alignItems="center" gap="$2">
              <MaterialCommunityIcons name="check-circle" size={20} color={colors.success} />
              <YStack>
                <Text color={colors.text} fontSize="$2" fontWeight="600">
                  {stats.data.overview.completedRequests}
                </Text>
                <Text color={colors.textSecondary} fontSize="$1">
                  Completed
                </Text>
              </YStack>
            </XStack>
          </Card>
        </XStack>
      )}

      {/* Popularity Graph */}
      {showPopularityGraph && (
        <Animatable.View animation="fadeInDown" duration={300}>
          <Card backgroundColor={colors.cardBackground} padding="$4">
            <XStack justifyContent="space-between" alignItems="center" marginBottom="$3">
              <H4 color={colors.text} fontSize="$4">
                Popular Integration Requests
              </H4>
              <Button
                size="$2"
                variant="outlined"
                borderColor={colors.border}
                onPress={() => setShowPopularityGraph(false)}
              >
                <AntDesign name="close" size={14} color={colors.text} />
              </Button>
            </XStack>

            {/* Category Filter */}
            <XStack gap="$2" marginBottom="$3" flexWrap="wrap">
              <Button
                size="$2"
                variant={selectedCategory === 'all' ? 'solid' : 'outlined'}
                backgroundColor={selectedCategory === 'all' ? colors.accent : 'transparent'}
                borderColor={colors.border}
                onPress={() => setSelectedCategory('all')}
              >
                <Text color={selectedCategory === 'all' ? 'white' : colors.text}>
                  All
                </Text>
              </Button>
              {categoryOptions.map((category) => (
                <Button
                  key={category.value}
                  size="$2"
                  variant={selectedCategory === category.value ? 'solid' : 'outlined'}
                  backgroundColor={selectedCategory === category.value ? colors.accent : 'transparent'}
                  borderColor={colors.border}
                  onPress={() => setSelectedCategory(category.value)}
                >
                  <Text color={selectedCategory === category.value ? 'white' : colors.text}>
                    {category.label}
                  </Text>
                </Button>
              ))}
            </XStack>

            {/* Popular Requests List */}
            {isLoadingPopular ? (
              <XStack justifyContent="center" padding="$4">
                <Spinner size="large" color={colors.accent} />
              </XStack>
            ) : (
                <ScrollView 
                  style={{ maxHeight: 400, gap: 10 }} 
                  contentContainerStyle={{ gap: 10, paddingBottom: 10 }}
                  showsVerticalScrollIndicator={true}
                  nestedScrollEnabled={true}
                >
                {popularRequests?.data?.slice(0, 5).map((request: IntegrationRequest, index: number) => (
                  <Animatable.View
                    key={request._id}
                    animation="fadeInUp"
                    delay={index * 100}
                    duration={300}
                  >
                    <Card 
                      backgroundColor={colors.background} 
                      padding="$3"
                      borderWidth={1}
                      borderColor={colors.border}
                    >
                      <XStack justifyContent="space-between" alignItems="flex-start">
                        <YStack flex={1} gap="$2">
                          <XStack justifyContent="space-between" alignItems="center">
                            <H4 color={colors.text} fontSize="$3" numberOfLines={1}>
                              {request.title}
                            </H4>
                            <Badge
                              backgroundColor={
                                request.status === 'completed' ? colors.success :
                                request.status === 'in_progress' ? colors.accent :
                                request.status === 'rejected' ? colors.error :
                                colors.warning
                              }
                              color="white"
                              size="$1"
                            >
                              {request.status.replace('_', ' ').toUpperCase()}
                            </Badge>
                          </XStack>
                          
                          <Text color={colors.textSecondary} fontSize="$2" numberOfLines={2}>
                            {request.description}
                          </Text>
                          
                          <XStack justifyContent="space-between" alignItems="center">
                            <XStack gap="$3" alignItems="center">
                              <XStack alignItems="center" gap="$1">
                                <MaterialCommunityIcons 
                                  name="account" 
                                  size={14} 
                                  color={colors.textSecondary} 
                                />
                                <Text color={colors.textSecondary} fontSize="$1">
                                  {request.requestedBy.firstName} {request.requestedBy.lastName}
                                </Text>
                              </XStack>
                              
                              <XStack alignItems="center" gap="$1">
                                <MaterialCommunityIcons 
                                  name="tag" 
                                  size={14} 
                                  color={colors.textSecondary} 
                                />
                                <Text color={colors.textSecondary} fontSize="$1">
                                  {request.category}
                                </Text>
                              </XStack>
                            </XStack>
                            
                            <XStack alignItems="center" gap="$2">
                              <XStack alignItems="center" gap="$1">
                                <Button
                                  size="$2"
                                  variant={request.userVote === 'up' ? 'solid' : 'outlined'}
                                  backgroundColor={request.userVote === 'up' ? colors.success : 'transparent'}
                                  borderColor={colors.border}
                                  onPress={() => handleVote(request._id, 'up')}
                                  disabled={voteMutation.isPending}
                                >
                                  <AntDesign 
                                    name="up" 
                                    size={12} 
                                    color={request.userVote === 'up' ? 'white' : colors.text} 
                                  />
                                </Button>
                                
                                <Text color={colors.text} fontSize="$2" fontWeight="600">
                                  {request.voteCount}
                                </Text>
                                
                                <Button
                                  size="$2"
                                  variant={request.userVote === 'down' ? 'solid' : 'outlined'}
                                  backgroundColor={request.userVote === 'down' ? colors.error : 'transparent'}
                                  borderColor={colors.border}
                                  onPress={() => handleVote(request._id, 'down')}
                                  disabled={voteMutation.isPending}
                                >
                                  <AntDesign 
                                    name="down" 
                                    size={12} 
                                    color={request.userVote === 'down' ? 'white' : colors.text} 
                                  />
                                </Button>
                              </XStack>
                            </XStack>
                          </XStack>
                        </YStack>
                      </XStack>
                    </Card>
                  </Animatable.View>
                ))}
                </ScrollView>
            )}
          </Card>
        </Animatable.View>
      )}

      {/* Request Form Dialog */}
      <Dialog open={showRequestForm} onOpenChange={setShowRequestForm}>
        <Dialog.Portal>
          <Dialog.Overlay
            animation="quick"
            opacity={0.6}
            style={{
              zIndex: 1000,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              ...(Platform.OS === 'web' && {
                backdropFilter: 'blur(8px)',
              })
            }}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Dialog.Content
            bordered
            elevate
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            x={0}
            scale={1}
            style={[
              {
                padding: 0,
                minWidth: 500,
                maxWidth: 600,
                maxHeight: "85%",
                borderRadius: 24,
                overflow: 'hidden',
                elevation: 12,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 12 },
                shadowOpacity: 0.15,
                shadowRadius: 32,
                backgroundColor: colors.background,
                borderWidth: 1,
                borderColor: colors.border,
              },
              Platform.OS === 'web' && {
                backdropFilter: 'blur(24px)',
                boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
              }
            ]}
            opacity={1}
            y={0}
          >
            {/* Header */}
            <View style={{
              backgroundColor: colors.cardBackground,
              paddingHorizontal: 24,
              paddingVertical: 20,
              borderBottomWidth: 1,
              borderBottomColor: colors.border,
            }}>
              <XStack alignItems="center" gap="$3">
                <View style={{
                  backgroundColor: colors.accent + '15',
                  borderRadius: 12,
                  padding: 10,
                }}>
                  <Feather
                    name="plus-circle"
                    size={20}
                    color={colors.accent}
                  />
                </View>
                <YStack flex={1}>
                  <Dialog.Title style={{
                    fontSize: 20,
                    fontWeight: '700',
                    color: colors.text,
                    marginBottom: 4,
                    margin: 0,
                  }}>
                    Request New Integration
                  </Dialog.Title>
                  <Dialog.Description style={{
                    fontSize: 14,
                    color: colors.textSecondary,
                    fontWeight: '500',
                    margin: 0,
                  }}>
                    Submit a request for a new integration
                  </Dialog.Description>
                </YStack>
              </XStack>
            </View>

            {/* Content */}
            <ScrollView style={{ padding: 24 }} showsVerticalScrollIndicator={false}>
              <YStack gap="$4">
                {/* Title */}
                <YStack gap="$2">
                  <SizableText 
                    color={colors.text} 
                    fontWeight="600"
                    size="$4"
                  >
                    Integration Title *
                  </SizableText>
                  <Input
                    value={formData.title}
                    onChangeText={(text) => setFormData(prev => ({ ...prev, title: text }))}
                    placeholder="e.g., Slack Integration"
                    backgroundColor={colors.cardBackground}
                    borderColor={colors.border}
                    color={colors.text}
                    placeholderTextColor={colors.textSecondary}
                    style={{
                      fontSize: 16,
                      padding: 12,
                      borderRadius: 12,
                      fontWeight: '500',
                    }}
                  />
                </YStack>

                {/* Description */}
                <YStack gap="$2">
                  <SizableText 
                    color={colors.text} 
                    fontWeight="600"
                    size="$4"
                  >
                    Description *
                  </SizableText>
                  <TextArea
                    value={formData.description}
                    onChangeText={(text) => setFormData(prev => ({ ...prev, description: text }))}
                    placeholder="Describe what this integration should do..."
                    backgroundColor={colors.cardBackground}
                    borderColor={colors.border}
                    color={colors.text}
                    placeholderTextColor={colors.textSecondary}
                    minHeight={100}
                    style={{
                      fontSize: 16,
                      padding: 12,
                      borderRadius: 12,
                      fontWeight: '500',
                    }}
                  />
                </YStack>

                {/* Category */}
                <YStack gap="$2">
                  <SizableText 
                    color={colors.text} 
                    fontWeight="600"
                    size="$4"
                  >
                    Category
                  </SizableText>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  >
                    <Select.Trigger 
                      backgroundColor={colors.cardBackground} 
                      borderColor={colors.border}
                      style={{
                        fontSize: 16,
                        padding: 12,
                        borderRadius: 12,
                        fontWeight: '500',
                      }}
                    >
                      <Select.Value placeholder="Select category" />
                    </Select.Trigger>
                    <Adapt when="sm" platform="touch">
                      <Sheet modal dismissOnSnapToBottom>
                        <Sheet.Frame>
                          <Sheet.ScrollView>
                            <Adapt.Contents />
                          </Sheet.ScrollView>
                        </Sheet.Frame>
                        <Sheet.Overlay />
                      </Sheet>
                    </Adapt>
                    <Select.Content zIndex={200000}>
                      <Select.ScrollUpButton />
                      <Select.Viewport>
                        {categoryOptions.map((category, index) => (
                          <Select.Item key={index} value={category.value}>
                            <Select.ItemText>{category.label}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.Viewport>
                      <Select.ScrollDownButton />
                    </Select.Content>
                  </Select>
                </YStack>

                {/* Tags */}
                <YStack gap="$2">
                  <SizableText 
                    color={colors.text} 
                    fontWeight="600"
                    size="$4"
                  >
                    Tags (comma-separated)
                  </SizableText>
                  <Input
                    value={formData.tags}
                    onChangeText={(text) => setFormData(prev => ({ ...prev, tags: text }))}
                    placeholder="e.g., messaging, team, collaboration"
                    backgroundColor={colors.cardBackground}
                    borderColor={colors.border}
                    color={colors.text}
                    placeholderTextColor={colors.textSecondary}
                    style={{
                      fontSize: 16,
                      padding: 12,
                      borderRadius: 12,
                      fontWeight: '500',
                    }}
                  />
                </YStack>

                {/* API Endpoint */}
                <YStack gap="$2">
                  <SizableText 
                    color={colors.text} 
                    fontWeight="600"
                    size="$4"
                  >
                    API Endpoint (optional)
                  </SizableText>
                  <Input
                    value={formData.apiEndpoint}
                    onChangeText={(text) => setFormData(prev => ({ ...prev, apiEndpoint: text }))}
                    placeholder="https://api.example.com"
                    backgroundColor={colors.cardBackground}
                    borderColor={colors.border}
                    color={colors.text}
                    placeholderTextColor={colors.textSecondary}
                    style={{
                      fontSize: 16,
                      padding: 12,
                      borderRadius: 12,
                      fontWeight: '500',
                    }}
                  />
                </YStack>

                {/* Documentation */}
                <YStack gap="$2">
                  <SizableText 
                    color={colors.text} 
                    fontWeight="600"
                    size="$4"
                  >
                    Documentation Link (optional)
                  </SizableText>
                  <Input
                    value={formData.documentation}
                    onChangeText={(text) => setFormData(prev => ({ ...prev, documentation: text }))}
                    placeholder="https://docs.example.com"
                    backgroundColor={colors.cardBackground}
                    borderColor={colors.border}
                    color={colors.text}
                    placeholderTextColor={colors.textSecondary}
                    style={{
                      fontSize: 16,
                      padding: 12,
                      borderRadius: 12,
                      fontWeight: '500',
                    }}
                  />
                </YStack>

                {/* Complexity and Business Value */}
                <XStack gap="$3">
                  <YStack flex={1} gap="$2">
                    <SizableText 
                      color={colors.text} 
                      fontWeight="600"
                      size="$4"
                    >
                      Estimated Complexity
                    </SizableText>
                    <RadioGroup
                      value={formData.estimatedComplexity}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, estimatedComplexity: value }))}
                    >
                      {complexityOptions.map((option) => (
                        <XStack key={option.value} alignItems="center" gap="$2">
                          <RadioGroup.Item value={option.value}>
                            <RadioGroup.Indicator />
                          </RadioGroup.Item>
                          <Label color={colors.text} fontSize="$3" fontWeight="500">
                            {option.label}
                          </Label>
                        </XStack>
                      ))}
                    </RadioGroup>
                  </YStack>

                  <YStack flex={1} gap="$2">
                    <SizableText 
                      color={colors.text} 
                      fontWeight="600"
                      size="$4"
                    >
                      Business Value
                    </SizableText>
                    <RadioGroup
                      value={formData.businessValue}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, businessValue: value }))}
                    >
                      {businessValueOptions.map((option) => (
                        <XStack key={option.value} alignItems="center" gap="$2">
                          <RadioGroup.Item value={option.value}>
                            <RadioGroup.Indicator />
                          </RadioGroup.Item>
                          <Label color={colors.text} fontSize="$3" fontWeight="500">
                            {option.label}
                          </Label>
                        </XStack>
                      ))}
                    </RadioGroup>
                  </YStack>
                </XStack>
              </YStack>
            </ScrollView>

            {/* Footer Actions */}
            <View style={{
              backgroundColor: colors.cardBackground,
              paddingHorizontal: 24,
              paddingVertical: 16,
              borderTopWidth: 1,
              borderTopColor: colors.border,
            }}>
              <XStack gap="$3" justifyContent="flex-end">
                <Dialog.Close asChild>
                  <Pressable
                    onPress={() => setShowRequestForm(false)}
                    style={{
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: colors.cardBackground,
                      borderWidth: 1,
                      borderColor: colors.border,
                      minWidth: 100,
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{
                      color: colors.textSecondary,
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Cancel
                    </Text>
                  </Pressable>
                </Dialog.Close>
                <Pressable
                  onPress={handleSubmitRequest}
                  disabled={createRequestMutation.isPending}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: colors.accent,
                    minWidth: 100,
                    alignItems: 'center',
                    elevation: 2,
                    shadowColor: colors.accent,
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.2,
                    shadowRadius: 8,
                    opacity: createRequestMutation.isPending ? 0.7 : 1,
                  }}
                >
                  {createRequestMutation.isPending ? (
                    <Spinner size="small" color="white" />
                  ) : (
                    <Text style={{
                      color: '#ffffff',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Submit Request
                    </Text>
                  )}
                </Pressable>
              </XStack>
            </View>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    </YStack>
  );
};

export default IntegrationRequestHeader;
